import { ImageRecord } from "common/records/Image";
import { RootState } from "common/reducers";
import { ids } from "common/selectors/idsManagement.selectors";
import { Nullable } from "common/types";
import { List, Map } from "immutable";
import { createSelector } from "reselect";

const rootKey = "images";

/**
 * Loads byId key from images
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<number, ImageRecord>} Map<number, ImageRecord>
 */
export const byId = (state: RootState) => state.getIn([rootKey, "byId"]);

/**
 * Loads byId key from images
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<number, ImageRecord>} Map<number, ImageRecord>
 */
export const imageOverviewById = (state: RootState) => state.getIn([rootKey, "imageOverviewsById"]);

/**
 * Loads an image by id
 *
 * @param {RootState} state The RootState
 * @param {{ imageId: number }} options
 * @returns
 */
export const image = (state: RootState, props: { imageId: Nullable<number> }) => {
  if (!props.imageId) {
    return null;
  }
  return byId(state).get(props.imageId);
};

/**
 * Loads an image overview by id
 *
 * @param {RootState} state The RootState
 * @param {{ imageId: number }} options
 * @returns
 */
export const imageOverview = (state: RootState, props: { imageId: number }) =>
  imageOverviewById(state).get(props.imageId);

export const imageUrlFromId = (
  overviewById: Map<number, ImageRecord>,
  imagesById: Map<number, ImageRecord>,
  id: number,
): Nullable<string> => overviewById.get(id)?.file?.url ?? imagesById.get(id)?.file?.url;

/**
 * Resolves catalog image ids to their URLs from the loaded image store. Product images land in
 * imageOverviewsById; option images land in byId — so check the overview slice first, then byId.
 * Unresolvable ids are dropped.
 *
 * @param state
 * @param imageIds
 * @returns
 */
export const imageUrlsFromIds = (state: RootState, imageIds: List<number>): List<string> => {
  const overviewById = imageOverviewById(state);
  const imagesById = byId(state);
  return imageIds.map((id) => imageUrlFromId(overviewById, imagesById, id)).filter((u): u is string => !!u);
};

/**
 * Loads an image loading flag by id
 *
 * @param {RootState} state The RootState
 * @param {{ imageId: number }} options
 * @returns {boolean} boolean
 */
export const imageLoading = (state: RootState, props: { imageId: number }) =>
  Boolean(image(state, props)?.get("loading"));

/**
 * Loads the batch image loading flag
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {boolean} boolean
 */
export const batchImageLoading = (state: RootState, _props?: any) => state.getIn([rootKey, "loading"]);

/**
 * Loads the count of images uploaded by "customer only"
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {number} number
 */
export const customerImagesCount = (state: RootState, _props?: any) => state.getIn([rootKey, "successCount"]);

/**
 * Loads the list of image ids
 *
 * @param {RootState} state The RootState
 * @returns {List<IdType>} List of ids
 */
export const imageIds = (state: RootState) => ids(state, { modelName: rootKey }) as List<number>;

/**
 * Evaluates if all images have the given `displayProp` set to true
 *
 * @param {RootState} state The RootState
 * @returns {boolean} The boolean value
 */
export const imagesDisplayChecked = createSelector([imageIds, byId, (state, props) => props], (ids, byId, props) => {
  return ids.every((id) => byId.get(id)?.get(props.displayProp) === true);
});

/**
 * Loads the customersUploadStatusByTaskId Map"
 *
 * @param {RootState} state The RootState
 * @returns {Map<string,string>} Map<string,string>
 */
export const customersUploadStatusByTaskId = (state: RootState) =>
  state.getIn([rootKey, "customersUploadStatusByTaskId"]);

/**
 * Returns false if any of the keys have value other than "finished" or "error"
 *
 * @param {RootState} state The RootState
 * @returns {boolean} boolean
 */
export const customerImageUploadInProgress = createSelector(customersUploadStatusByTaskId, (uploadStatusMap) => {
  if (!uploadStatusMap) {
    return false;
  }
  for (const status of uploadStatusMap.values()) {
    if (status !== "finished" && status !== "error") {
      return true;
    }
  }
  return false;
});

/**
 * Loads the customer image error state from redux"
 *
 * @param {RootState} state The RootState
 * @returns {boolean} boolean
 */
export const customerImagesError = (state: RootState, _props?: any) => state.getIn([rootKey, "customerImageError"]);
